import { twoFactorApi } from '@/api/twoFactor';
import { Field } from '@/components/Fields';
import { getUserInfo } from '@/features/auth';
import { useAppDispatch } from '@/hooks';
import {
  Alert,
  AlertIcon,
  Button,
  Center,
  Link as ChakraLink,
  Flex,
  FormControl,
  Heading,
  Text,
  useToast,
  VStack
} from '@chakra-ui/react';
import { Form, Formik, FormikHelpers } from 'formik';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import Session from 'supertokens-web-js/recipe/session';
import * as Yup from 'yup';

interface RecoveryFormValues {
  recoveryCode: string;
}

interface TwoFactorRecoveryViewProps {
  onSuccess: () => void;
  onBackToTotp: () => void;
}

export const TwoFactorRecoveryView: React.FC<TwoFactorRecoveryViewProps> = ({
  onSuccess,
  onBackToTotp,
}) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const [errorMessage, setErrorMessage] = useState('');
  const [remainingCodes, setRemainingCodes] = useState<number | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleSubmit = async (
    values: RecoveryFormValues,
    { setSubmitting }: FormikHelpers<RecoveryFormValues>,
  ) => {
    setSubmitting(true);
    setErrorMessage('');

    try {
      const response = await twoFactorApi.verifyRecoveryCode(
        values.recoveryCode.toUpperCase().replace(/[^A-Z0-9-]/g, '')
      );

      if (response.status === 'OK') {
        if (response.remainingCodes !== undefined) {
          setRemainingCodes(response.remainingCodes);
          
          if (response.remainingCodes === 0) {
            toast({
              title: 'No Recovery Codes Remaining',
              description: 'This was your last recovery code. If you lose access to your authenticator device, you must contact an administrator to reset your 2FA.',
              status: 'error',
              duration: 10000,
              isClosable: true,
              position: 'top',
            });
          } else if (response.remainingCodes <= 2) {
            toast({
              title: 'Low Recovery Codes Warning',
              description: `You only have ${response.remainingCodes} recovery code${response.remainingCodes !== 1 ? 's' : ''} remaining. Contact an administrator to reset your 2FA if you run out.`,
              status: 'warning',
              duration: 10000,
              isClosable: true,
              position: 'top',
            });
          }
        }
        await dispatch(getUserInfo());
        onSuccess();
        router.push('/results');
      } else {
        setErrorMessage(response.message || 'Invalid recovery code.');
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } } };
      const message = axiosError?.response?.data?.message;
      setErrorMessage(message || 'Recovery failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignOut = async () => {
    setIsLoggingOut(true);
    try {
      await Session.signOut();
      router.push('/');
    } catch (error) {
      console.error('Error signing out:', error);
      toast({
        title: 'Error',
        description: 'Failed to sign out. Please try again.',
        status: 'error',
        duration: 3000,
        position: 'top',
      });
      setIsLoggingOut(false);
    }
  };

  const RecoverySchema = Yup.object().shape({
    recoveryCode: Yup.string()
      .matches(/^[A-Za-z0-9]{4}-[A-Za-z0-9]{4}$/, 'Invalid recovery code format (XXXX-XXXX)')
      .required('Required'),
  });

  return (
    <Center>
      <VStack gap={8}>
        <VStack gap={2}>
          <Heading textAlign="center">Use Recovery Code</Heading>
          <Text color="gray.400" textAlign="center" maxW="350px">
            Enter one of your recovery codes to access your account
          </Text>
        </VStack>

        <Alert status="warning" borderRadius="md" maxW="350px">
          <AlertIcon />
          <Text fontSize="sm" color="gray.900">
            Each recovery code can only be used once. Keep your remaining codes safe.
          </Text>
        </Alert>

        <Formik
          initialValues={{ recoveryCode: '' }}
          onSubmit={handleSubmit}
          validationSchema={RecoverySchema}
        >
          {({ isSubmitting, isValid, dirty, errors, touched, setFieldValue }) => (
            <Form>
              <FormControl alignItems="center" display="flex" flexDir="column">
                <Field
                  type="text"
                  onDark
                  placeholder="XXXX-XXXX"
                  name="recoveryCode"
                  errors={errors.recoveryCode}
                  touched={touched.recoveryCode}
                  minW={{ base: 'unset', sm: '350px' }}
                  maxW="350px"
                  h="56px"
                  pl={3}
                  textAlign="center"
                  fontSize="xl"
                  letterSpacing="0.2em"
                  fontFamily="mono"
                  textTransform="uppercase"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    let value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                    if (value.length > 4) {
                      value = value.slice(0, 4) + '-' + value.slice(4, 8);
                    }
                    setFieldValue('recoveryCode', value);
                  }}
                  full
                />

                {errorMessage && (
                  <Text mt={4} mb={2} color="red.400" textAlign="center">
                    {errorMessage}
                  </Text>
                )}

                <Button
                  fontWeight="semibold"
                  variant="primary"
                  size="xl"
                  w="full"
                  minW={{ base: 'unset', sm: '350px' }}
                  maxW="350px"
                  mt={6}
                  type="submit"
                  isDisabled={!isValid || !dirty || isSubmitting}
                  isLoading={isSubmitting}
                >
                  Verify Recovery Code
                </Button>
              </FormControl>
            </Form>
          )}
        </Formik>

        <Flex gap={1} flexDir="column" alignItems="center">
          <ChakraLink
            color="teal.500"
            cursor="pointer"
            onClick={onBackToTotp}
            fontSize="sm"
          >
            Back to authenticator code
          </ChakraLink>
        </Flex>

        <Flex gap={1} flexDir="column" alignItems="center" mt={4}>
          <ChakraLink
            color="gray.500"
            cursor={isLoggingOut ? 'not-allowed' : 'pointer'}
            onClick={isLoggingOut ? undefined : handleSignOut}
            fontSize="sm"
            opacity={isLoggingOut ? 0.5 : 1}
            pointerEvents={isLoggingOut ? 'none' : 'auto'}
            _hover={{ color: 'gray.400' }}
          >
            {isLoggingOut ? 'Signing out...' : 'Sign in with a different account'}
          </ChakraLink>
        </Flex>
      </VStack>
    </Center>
  );
};
